import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, throwError } from 'rxjs';

import { APPOINTMENT_ROUTES } from '../consts/appointment-routes.const';
import { SessionService } from '../pages/appointment/authenticate/services/session.service';
import { dealerIdFromUrl } from '../services/start-redirect';
import { SKIP_AUTH_REDIRECT } from './auth-redirect.const';

const LOGOUT_URL = '/wp/planner/logout';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const sessionService: SessionService = inject(SessionService);
  const router: Router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse): Observable<never> => {
      const isUnauthorized: boolean = error.status === 401 || error.status === 403;
      const isLogoutRequest: boolean = req.url.includes(LOGOUT_URL);
      const skipAuthRedirect: boolean = req.context.get(SKIP_AUTH_REDIRECT);
      const isOnAuthenticatePage: boolean = router.url.startsWith(
        APPOINTMENT_ROUTES.AUTHENTICATE_URL,
      );

      // Een gast ("verder zonder inloggen") heeft bewust geen geverifieerde sessie. Een
      // 401/403 op een auth-only endpoint (bv. het klantprofiel ophalen) is dan verwacht
      // en mag de gast-sessie niet wissen of de gast naar start terugsturen — laat de fout
      // door zodat de aanroepende code 'm zelf afhandelt. De logout en de sessie-probe
      // (SKIP_AUTH_REDIRECT, gezet door SessionService.checkSession) verwachten een 401 óók
      // als normaal antwoord: laat die callers het afhandelen i.p.v. de sessie globaal te
      // wissen en te redirecten.
      if (!isUnauthorized || isLogoutRequest || skipAuthRedirect || sessionService.isGuest()) {
        return throwError((): HttpErrorResponse => error);
      }

      sessionService.clearSession();

      if (!isOnAuthenticatePage) {
        // Preserve ?dealerId= from the page we're leaving so the bounce to start
        // doesn't drop the dealer context (→ app.ts "missing dealer" gate).
        const dealerId: string | null = dealerIdFromUrl(router.url);
        router.navigateByUrl(
          dealerId
            ? `${APPOINTMENT_ROUTES.REDIRECT_URL}?dealerId=${dealerId}`
            : APPOINTMENT_ROUTES.REDIRECT_URL,
        );
      }

      return throwError((): HttpErrorResponse => error);
    }),
  );
};
